import { Divider, Paper, Theme, Typography, useTheme } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useState } from 'react';
import { currencyHelper } from '../../helpers/currencyHelper';
import { dateHelper } from '../../helpers/dateHelper';
import { linkHelper } from '../../helpers/linkHelper';
import { linkTypeEnumHelper } from '../../helpers/linkTypeEnumHelper';
import { numberHelper } from '../../helpers/numberHelper';
import { paymentTypeHelper } from '../../helpers/paymentTypeHelper';
import { Link } from '../../store/config/types';
import styles from '../../styles/_variables.module.scss';
import { ShareLinkDialog } from '../dialogs/ShareLinkDialog';
import CardFooter from './CardFooter';
import LinkStatusPill from './LinkStatusPill';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'column',
      padding: '1rem',
    },
    header: {
      color: theme.palette.secondary.main,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    row: { display: 'flex', alignItems: 'center' },
    date: {
      fontSize: '0.75rem',
      fontWeight: Number(styles.normalTextWeight),
      color: styles.darkBlue,
    },
    body: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '0.8rem',
      '& svg': {
        color: theme.palette.primary.main,
        fontSize: '1.3rem',
      },
    },
    amountContainer: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.35rem',
    },
    text: {
      marginBlockStart: 0,
      marginBlockEnd: 0,
      fontSize: '0.875rem',
      fontWeight: Number(styles.normalTextWeight),
      color: styles.darkBlue,
    },
    descriptionText: {
      maxWidth: '70%',
      textAlign: 'center',
      height: '2.5rem',
      display: 'flex',
      alignItems: 'center',
    },
    expirationText: {
      fontWeight: 600,
      color: styles.slateBlue,
    },
    amount: {
      color: styles.darkBlue,
      fontSize: '1.5rem',
      fontWeight: 500,
    },
    divider: {
      margin: '0 0.5rem',
      alignSelf: 'center',
      height: '1.6rem',
    },
  }),
);

interface LinkCardProps {
  link: Link;
  setLinkToDelete(link: Link): void;
}

function LinkCard({ link, setLinkToDelete }: LinkCardProps) {
  const classes = useStyles(useTheme());
  const [shareOpen, setShareOpen] = useState<boolean>(false);
  const totalAmount = linkHelper.getTotalLinkAmount(link);

  return (
    <>
      <Paper elevation={1} className={classes.container}>
        <div className={classes.header}>
          <div className={classes.row}>
            {linkTypeEnumHelper.getIcon(link.linkType)}
            <Divider className={classes.divider} orientation="vertical" />
            <span>{paymentTypeHelper.paymentTypesTitle[link.paymentType]}</span>
          </div>
          <LinkStatusPill link={link} />
        </div>
        <div className={classes.body}>
          <div className={classes.amountContainer}>
            <Typography className={classes.amount}>
              {currencyHelper.getCurrencyToken(link.amount.currency)}{' '}
              {numberHelper.getNumberWithDots(totalAmount)}
            </Typography>
            <p className={`${classes.text} ${classes.descriptionText}`}>
              {link.description || 'Sin descripción'}
            </p>

            <p className={classes.text}>
              <span className={classes.expirationText}>Vencimiento&nbsp;</span>
              <span>
                {link.validUntil ? dateHelper.toShorterNiceString(link.validUntil) : 'Sin vencimiento'}
              </span>
            </p>
          </div>
        </div>
        <Divider />
        <CardFooter link={link} setShareOpen={setShareOpen} setLinkToDelete={setLinkToDelete} />
      </Paper>
      <ShareLinkDialog open={shareOpen} setOpen={setShareOpen} link={link} />
    </>
  );
}

export default LinkCard;
